import { CheckEmail, CheckUserName } from '@/common/services/realTimeCheckAPI';
import { API_ENDPOINTS } from '../../api/apiEndpoints';
import DynamicForm, { type DynamicFormSchema } from '@/common/components/DynamicForm/DynamicForm';
import { useRouter } from '@tanstack/react-router';
import { QUERY_KEYS } from '../../api/apiEndpoints';

export default function UserForm() {
    const { state } = useRouter().state.location;
    const record = (state as { record?: Record<string, unknown> })?.record;

    const userid = record && record.id !== undefined && record.id !== null
        ? Number(record.id)
        : undefined;

    const name =
        state && (state as { companyname?: string }).companyname !== undefined
            ? String((state as { companyname?: number }).companyname)
            : undefined;

    const UserCompanyId =
        state && (state as { companyId?: number }).companyId !== undefined
            ? Number((state as { companyId?: number }).companyId)
            : undefined;

    const userFormSchema: DynamicFormSchema = {
        layout: 'vertical',
        showResetButton: true,
        sections: [
            {
                key: 'user-details',
                title: 'User Form (' + name + ')',
                type: 'card',
                layout: { type: 'grid', gutter: 24 },
                fields: [
                    {
                        name: 'Firstname',
                        label: 'First Name',
                        type: 'input',
                        required: true,
                        requiredMessage: 'Please enter a First Name.',
                        autocomplete: 'false',
                        pattern: 'AlphabetsWithSpace',
                        validation: [
                            { min: 3, message: 'First Name must be at least 3 characters long.' },
                            { max: 50, message: 'First Name cannot be more than 50 characters long.' },
                        ],
                        colSpan: 12,
                    },
                    {
                        name: 'LastName',
                        label: 'Last Name',
                        type: 'input',
                        required: true,
                        requiredMessage: 'Please enter a Last Name.',
                        autocomplete: 'false',
                        pattern: 'AlphabetsWithSpace',
                        validation: [
                            { min: 3, message: 'Last Name must be at least 3 characters long.' },
                            { max: 50, message: 'Last Name cannot be more than 50 characters long.' },
                        ],
                        colSpan: 12,
                    },
                    {
                        name: 'cellNo',
                        label: 'Mobile Number',
                        type: 'input',
                        required: true,
                        colSpan: 12,
                        pattern: 'PhoneNumber',
                    },
                    {
                        name: 'userName',
                        label: 'Username',
                        type: 'input',
                        readOnlyWhen: [{ field: 'id', operator: '>', value: '0' }],
                        required: true,
                        requiredMessage: 'Please enter a username.',
                        autocomplete: 'false',
                        pattern: 'Alphabets',
                        validation: [
                            { min: 3, message: 'Name must be at least 3 characters long.' },
                            { max: 50, message: 'Name cannot be more than 50 characters long.' },
                            {
                                validator: async (_: any, value: string) => {
                                    if (!value) return Promise.resolve();
                                    if (value.length < 3) return Promise.resolve();

                                    try {
                                        const result = await CheckUserName(value, userid);
                                        if (result.exists) {
                                            return Promise.reject('UserName already exists.');
                                        }
                                        return Promise.resolve();
                                    } catch (error: any) {
                                        console.error('Error validating UserName:', error);
                                        return Promise.reject(error.message || 'Error validating UserName.');
                                    }
                                },
                            },
                        ],
                        hideOnEdit: false,
                        colSpan: 12,
                    },
                    {
                        name: 'email',
                        label: 'Email',
                        type: 'input',
                        required: true,
                        requiredMessage: 'An email is required to create a user.',
                        pattern: 'Email',
                        validation: [
                            { max: 256, message: 'Email cannot be more than 256 characters long.' },
                            {
                                validator: async (_: any, value: string) => {
                                    if (!value) return Promise.resolve();
                                    if (value.length < 1) return Promise.resolve();

                                    try {
                                        const result = await CheckEmail(value, userid);
                                        if (result.exists) {
                                            return Promise.reject('Email already exists.');
                                        }
                                        return Promise.resolve();
                                    } catch (error: any) {
                                        console.error('Error validating Email:', error);
                                        return Promise.reject(error.message || 'Error validating Email.');
                                    }
                                },
                            },
                        ],
                        autocomplete: 'false',
                        colSpan: 12,
                    },
                    {
                        name: 'previousEmail',
                        label: 'Email',
                        type: 'input',
                        autocomplete: 'false',
                        colSpan: 12,
                        hidden: true,
                    },
                    {
                        name: 'isActive',
                        label: 'Status',
                        hidden: true,
                        type: 'switch',
                        colSpan: 12,
                    },
                    // 👇 Hidden fixed values
                    {
                        name: 'userType',
                        label: '',
                        type: 'input',
                        hidden: true,
                        defaultValue: `company`,
                    },
                    {
                        name: 'roles',
                        label: '',
                        type: 'input',
                        hidden: true,
                        defaultValue: [2], // must be array of numbers
                    },
                    {
                        name: 'userTypeId',
                        label: '',
                        type: 'input',
                        hidden: true,
                        defaultValue: UserCompanyId,
                    },
                    // ✅ Added hidden companyId field
                    {
                        name: 'companyId',
                        label: '',
                        type: 'input',
                        hidden: true,
                        defaultValue: UserCompanyId,
                    },
                ],
            },
        ],
    };

    return (
        <DynamicForm
            schema={userFormSchema}
            submitApiUrl={API_ENDPOINTS.PARTICIPANTS.SAVE_USER}
            getByIdApiUrl={API_ENDPOINTS.PARTICIPANTS.GET_USER_BY_ID}
            updateApiUrl={API_ENDPOINTS.PARTICIPANTS.UPDATE_USER}
            refreshTableDataKey={QUERY_KEYS.PARTICIPANTS.GET_COMPANY_DETAIL}
            backUrl="history"
        />
    );
}